import React from 'react';
import './App.scss';
import { createBrowserRouter, createRoutesFromElements, defer, Navigate, Route, useLocation } from 'react-router-dom';
import HomePage from './pages/home/home.page';
import GlobalLayout from './components/layouts/global/global.layout';
import { AuthLayout } from './components/layouts/auth/auth.layout';
import ErrorPage from './pages/error-management/error/error.page';
import { useAuth } from './hooks/use-auth.hook';
import CreateAccountPage from './pages/account-management/create-account/create-account.page';
import ExpiredPage from './pages/error-management/expired/expired.page';
import ErrorAccountPage from './pages/account-management/create-account/error-account.page';
import AccountValidationPage from './pages/account-management/create-account/account-validation.page';
import ProfilePage from './pages/account-management/profile/profile.page';
import ResetPasswordPage from './pages/account-management/reset-password/reset-password.page';
import ContractAttachementPage from './pages/contract/contract-attachement/contract-attachement.page';
import ContractPage from './pages/contract/details-contract/contract.page';
import ListContractsPage from './pages/contract/list-contracts/list-contracts.page';
import ContactUsPage from './pages/contact-us/contact-us.page';
import MyBillsPage from './pages/contract/my-bills/my-bills.page';
import MyConsommationsPage from './pages/contract/my-consommations/my-consommations.page';
import MeterReadingPage from './pages/services/logged/meter-reading /meter-reading.page';
import OnLineProceduresPage from './pages/services/logged-off/on-line-procedures/on-line-procedures.page';
import AddressContractPage from './pages/services/logged/address-contract/address-contact.page';
import PageContentPage from './pages/admin/page/page-content.page';
import { CustomGoogleReCaptchaProvider } from './components/commun/google-recaptcha';
import { Cookies } from 'react-cookie-consent';
import DirectDebitPage from './pages/services/logged/direct-debit/direct-debit.page';
import MyRequestsPage from './pages/contract/my-requests/my-requests.page';
import MovingInBillPage from './pages/moving-in/moving-in-bill/moving-in-bill.page';
import MovingInBillContractPage from './pages/moving-in/moving-in-bill-contract/moving-in-bill-contract.page';
import MovingInStepOnePage from './pages/moving-in/moving-in-step-one/moving-in-step-one.page';
import EditContentPage from './pages/admin/edit-content/edit-content.page';
import MoveHousePage from './pages/services/logged/move-house/move-house.page';
import SearchUserPage from './pages/admin/search-user/search-user.page';
import MovingInStepTwoPage from './pages/moving-in/moving-in-step-two/moving-in-step-two.page';
import MovingInStepThreePage from './pages/moving-in/moving-in-step-three/moving-in-step-three.page';
import CommunLoggedOffPage from './pages/services/logged-off/commun-logged-off.page';
import MeterReadingLoggedOffPage from './pages/services/logged-off/meter-reading/meter-reading-logged-off.page';
import PaymentSchedulePage from './pages/contract/payment-schedule/payment-schedule.page';
import ConnectionRequestAcPage from './pages/services/logged-off/connection-request-ac/connection-request-ac.page';
import InformationPage from './pages/footer/information.page';
import LoginPage from './pages/account-management/login/login.page';
import InformationEtablissementPage from './pages/admin/etab-infos/etab-infos.page';
import PromotionMensualisationSecurityPage from './pages/promotion-mensualisation/promotion-mensualisation-security.page';
import PromotionMensualisationDirectDebitPage from './pages/promotion-mensualisation/promotion-mensualisation-direct-debit.page';

/**
 * Récupération des données de l'utilisateur depuis le stockage local
 */
const getUserData = () =>
    new Promise(resolve => {
        const user = window.localStorage.getItem('user');
        resolve(user);
    });

/**
 * Initialisation des routes web
 *
 * Pages entourées du layout
 *
 * Page 404 si la route ne correspond à aucun chemin défini
 */
export const App = createBrowserRouter(
    createRoutesFromElements(
        <Route element={<AuthLayout />} loader={() => defer({ userPromise: getUserData() })}>
            <Route element={<GlobalLayout />}>
                {/* Index */}
                <Route path="/" element={<HomePage />} />
                {/* Infos RGPD */}
                <Route key={'rgpd'} path={'donnees-personnelles'} element={<InformationPage page={'rgpd'} />} />
                {/* Infos cgu */}
                <Route key={'cgu'} path={'cgu'} element={<InformationPage page={'cgu'} />} />
                {/* Mentions légales */}
                <Route key={'notice'} path={'mentions-legales'} element={<InformationPage page={'notice'} />} />
                {/* Mentions légales */}
                <Route
                    key={'gestioncookies'}
                    path={'gestion-cookies'}
                    element={
                        // check cookies
                        <InformationPage page={'gestioncookies'} />
                    }
                />
                {/* Connexion, nécessite de ne pas déjà être connecté */}
                <Route
                    path="connexion"
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={false}>
                                <LoginPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Création de compte, nécessite de ne pas déjà être connecté */}
                <Route
                    path="creer-espace-usage"
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={false}>
                                {/*Utilisation de google recaptcha*/}
                                <CustomGoogleReCaptchaProvider>
                                    <CreateAccountPage />
                                </CustomGoogleReCaptchaProvider>
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Démarches en ligne, nécessite de ne pas déjà être connecté */}
                <Route
                    path="demarches-en-ligne"
                    element={
                        // check cookies
                        <CheckCookies>
                            <OnLineProceduresPage />
                        </CheckCookies>
                    }
                />
                {/* Démarches en ligne, nécessite de ne pas déjà être connecté */}
                <Route
                    path="demande-raccordement-ac"
                    element={
                        // check cookies
                        <CheckCookies>
                            <CustomGoogleReCaptchaProvider>
                                <ConnectionRequestAcPage />
                            </CustomGoogleReCaptchaProvider>
                        </CheckCookies>
                    }
                />

                {/* Page de sécurité pour promotion de mensualisation */}
                <Route path="promotion-mensualisation" element={<PromotionMensualisationSecurityPage />} />

                {/* Page de mensualisation via promotion sans compte */}
                <Route
                    path="promotion-mensualisation/mensualisation"
                    element={<PromotionMensualisationDirectDebitPage />}
                />

                {/* Démarches en ligne, nécessite de ne pas déjà être connecté */}
                <Route
                    path="j-emmenage"
                    element={
                        // check cookies
                        <CheckCookies>
                            <MovingInBillPage />
                        </CheckCookies>
                    }
                />
                {/* Démarches en ligne, nécessite de ne pas déjà être connecté */}
                <Route
                    path="j-emmenage/rattachement-contrat"
                    element={
                        // check cookies
                        <CheckCookies>
                            <MovingInBillContractPage />
                        </CheckCookies>
                    }
                />
                {/* Démarches en ligne, j'emmenage etape 1 */}
                <Route
                    path="j-emmenage/etape-une"
                    element={
                        // check cookies
                        <CheckCookies>
                            <MovingInStepOnePage />
                        </CheckCookies>
                    }
                />
                {/* Je déménage étape deux */}
                <Route
                    path="j-emmenage/etape-deux"
                    element={
                        // check cookies
                        <CheckCookies>
                            <MovingInStepTwoPage />
                        </CheckCookies>
                    }
                />

                {/* Emmenagement, dernière étape du tunnel*/}
                <Route
                    path="j-emmenage/etape-trois"
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            {/*Utilisation de google recaptcha*/}
                            <CustomGoogleReCaptchaProvider>
                                <MovingInStepThreePage />
                            </CustomGoogleReCaptchaProvider>
                        </CheckCookies>
                    }
                />

                {/* Page Profil, nécessite d'être connecté */}
                <Route
                    path={'profil'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <ProfilePage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Rattachement de contrat, nécessite d'être connecté */}
                <Route
                    path={'rattacher-un-contrat'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <ContractAttachementPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Infos d'un contrat, nécessite d'être connecté */}
                <Route
                    path={'contrat/:id'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <ContractPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Infos d'un contrat, nécessite d'être connecté */}
                <Route
                    path={'contrat/:id/echeances'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <PaymentSchedulePage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Infos d'un contrat, nécessite d'être connecté */}
                <Route
                    path={'contrat/:id/demandes'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <ContractPage tab={3} />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/*Route pour le releve de compteur*/}
                <Route
                    path={'contrat/:id/releve-mon-compteur'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <MeterReadingPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                <Route
                    path={'contrat/:id/nouvelle-adresse-contrat'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <AddressContractPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />

                <Route
                    path={'contrat/:id/demenage'}
                    element={
                        <CheckAuth logged={true}>
                            <MoveHousePage />
                        </CheckAuth>
                    }
                />
                {/* Liste des contrats, nécessite d'être connecté */}
                <Route
                    path={'mes-contrats'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <ListContractsPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Liste des consommations, nécessite d'être connecté */}
                <Route
                    path={'mes-consommations'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <MyConsommationsPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Liste des factures, nécessite d'être connecté */}
                <Route
                    path={'mes-factures'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <MyBillsPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Route pour prélévement automatique*/}
                <Route
                    path={'contrat/:id/prelevement-automatique'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <DirectDebitPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/* Liste des demandes, nécessite d'être connecté */}
                <Route
                    path={'mes-demandes'}
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*check if is auth*/}
                            <CheckAuth logged={true}>
                                <MyRequestsPage />
                            </CheckAuth>
                        </CheckCookies>
                    }
                />
                {/*Edition de contenu admin*/}
                <Route
                    path={'administration/pages/:id'}
                    element={
                        // check cookies
                        <CheckAdminOrComm>
                            <EditContentPage />
                        </CheckAdminOrComm>
                    }
                />
                {/* Reset du mdp */}
                <Route path="/reinitialisation-mot-de-passe/:token" element={<ResetPasswordPage />} />
                {/* Administration des pages */}
                <Route
                    path="/administration/pages"
                    element={
                        // check if user is admin
                        <CheckAdminOrComm>
                            <PageContentPage />
                        </CheckAdminOrComm>
                    }
                />
                {/* Administration recherche utilisateurs */}
                <Route
                    path="/administration/recherche-abonne"
                    element={
                        // check if user is admin
                        <CheckAdmin>
                            <SearchUserPage />
                        </CheckAdmin>
                    }
                />
                {/* Administration recherche etablissement */}
                <Route
                    path="/administration/information-etablissement"
                    element={
                        // check if user is admin
                        <CheckAdminOrComm>
                            <InformationEtablissementPage />
                        </CheckAdminOrComm>
                    }
                />
                {/* Page de contact */}
                <Route
                    path="/contactez-nous"
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*Utilisation de google recaptcha*/}
                            <CustomGoogleReCaptchaProvider>
                                <ContactUsPage />
                            </CustomGoogleReCaptchaProvider>
                        </CheckCookies>
                    }
                />
                {/* Page de paiement facture */}
                <Route
                    path="/paiement-facture"
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*Utilisation de google recaptcha*/}
                            <CustomGoogleReCaptchaProvider>
                                <CommunLoggedOffPage page="payment" />
                            </CustomGoogleReCaptchaProvider>
                        </CheckCookies>
                    }
                />
                {/* Page de paiement facture */}
                <Route
                    path="/releve-compteur"
                    element={
                        // check cookies
                        <CheckCookies>
                            {/*Utilisation de google recaptcha*/}
                            <CustomGoogleReCaptchaProvider>
                                <CommunLoggedOffPage page="meter-reading" />
                            </CustomGoogleReCaptchaProvider>
                        </CheckCookies>
                    }
                />
                <Route
                    path="/releve-compteur/validation"
                    element={
                        // check cookies
                        <CheckCookies>
                            <MeterReadingLoggedOffPage />
                        </CheckCookies>
                    }
                />
                <Route
                    path={'/identifiant-perdu'}
                    element={
                        <CheckCookies>
                            <CustomGoogleReCaptchaProvider>
                                <CommunLoggedOffPage page="login-forgot" />
                            </CustomGoogleReCaptchaProvider>
                        </CheckCookies>
                    }
                />
            </Route>

            {/* Route de validation du compte */}
            <Route path="/validation-compte/:token" element={<AccountValidationPage />} />

            {/* Routes erreurs */}
            {/* Page lien expiré */}
            <Route path="lien-expire" element={<ExpiredPage />} />
            {/* Echec Validation de compte */}
            <Route path="creation-compte-invalide" element={<ErrorAccountPage />} />
            {/* Pages erreur 404, soit par lien inconnu, soit par ressource non trouvée */}
            <Route path="erreur-404" element={<ErrorPage />} />
            <Route path="*" element={<ErrorPage />} />
        </Route>
    )
);

/**
 * Fonction vérifiant si l'user peut accéder à la page donnée ou doit être redirigé en fonction de son état d'authentification
 * @param children Element qui sera retourné si l'état d'authentification est correct
 * @param logged Bool qui détermine si la page nécessite d'être connecté ou déconnecté
 * @constructor
 */
export function CheckAuth({ children, logged }: { children: JSX.Element; logged: boolean }) {
    let auth = useAuth();
    let location = useLocation();

    if (!logged) {
        if (auth.user) {
            //On redirige si la personne est connectée
            return <Navigate to="/" state={{ from: location }} replace />;
        }
    } else if (logged) {
        if (!auth.user) {
            // On redirige si la personne n'est pas connectée
            return <Navigate to={'/connexion'} state={{ from: location }} replace />;
        }
    }

    return children;
}

/**
 * Méthode qui permet de différentier un admin d'un utilisateur lambda
 * @param children composant à afficher
 * @constructor
 */
export function CheckAdmin({ children }: { children: JSX.Element }) {
    let auth = useAuth();
    let location = useLocation();

    if (!auth.user?.roles?.includes('ROLE_WEBSERVICE_USAGER_ADMIN')) {
        return <Navigate to={'/'} state={{ from: location }} replace />;
    }

    return children;
}

/**
 * Méthode qui permet de différentier un admin ou gestion,naire comm d'un utilisateur lambda
 * @param children composant à afficher
 * @constructor
 */
export function CheckAdminOrComm({ children }: { children: JSX.Element }) {
    let auth = useAuth();
    let location = useLocation();

    if (
        !auth.user?.roles?.includes('ROLE_WEBSERVICE_USAGER_ADMIN') &&
        !auth.user?.roles?.includes('ROLE_WEBSERVICE_USAGER_COMM')
    ) {
        return <Navigate to={'/'} state={{ from: location }} replace />;
    }

    return children;
}

/**
 * Méthode qui permet de savoir si oui ou non un utilisateur à accépté les cookies
 * @param children vue à afficher
 * @constructor
 */
export function CheckCookies({ children }: { children: JSX.Element }) {
    let acceptCookie = Cookies.get('acceptGoogle');
    let location = useLocation();

    if (acceptCookie === 'true') {
        return children;
    }
    //On redirige si la personne est connectée
    return <Navigate to="/" state={{ from: location }} replace />;
}
